import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { IonNav, ModalController } from '@ionic/angular';
import * as moment from 'moment';
import { CommonService } from 'src/app/services/common.service';
import { CaseService } from 'src/app/services/case.service';
import { Subscription } from 'rxjs';
import { AddressDetailsPage } from '../../../../address-details/address-details.page';
import { AuthService } from 'src/app/services/auth.service';
import { CustomFormPage } from 'src/app/master/custom-form/custom-form.page';
import { buildNachaVariables } from 'src/app/master/custom-form/nacha-variables';
import { DynamicFormsService } from 'src/app/services/dynamic-forms.service';

interface RepayApiResponse {
  result: string;
  message: string;
}

@Component({
  selector: 'app-repay-ach',
  templateUrl: './repay-ach.page.html',
  styleUrls: ['./repay-ach.page.scss'],
})
export class RepayAchPage implements OnInit, OnDestroy {
  @Input() caseData;
  @Input() planType = 'plan';
  @Input() selectedPlanData;
  caseId;
  @Input() debtorId = localStorage.getItem('debtor_id');
  selectedCases = [];
  debtorDetails;
  @Input() paymentMethod;
  addAchForm: FormGroup;
  datemin = moment().format('YYYY-MM-DD');
  datemax = moment().add('100', 'years').format('YYYY-MM-DD');
  savedAchList = [];
  debtorDetailsSubscription: Subscription;
  caseDetailsSubscription: Subscription;
  paymentGatewayList = [];
  savedAch = [];
  isGroupArrangement = false;
  caseList = [];
  groupArrId;
  billingAddress = [];
  selectedBillingAddress;
  saveBtnDisabled = true;
  paymentGatewayTemplate;
  amount;
  frequency;
  firstPaymentDate;
  numberOfPayments = 1;
  outstanding;
  showOption: boolean = false;
  checkoutFormId: string;
  paytoken: string;
  fieldLabels: any = {};
  achDisclosureEnabled = false;
  accountTypes = [
    { value: 'Checking', label: 'Checking' },
    { value: 'Savings', label: 'Savings' }
  ];

  constructor(
    private formBuilder: FormBuilder,
    private commonService: CommonService,
    private caseService: CaseService,
    private modalController: ModalController,
    private authService: AuthService,
    private ionNav: IonNav,
    private dynamicFormsService: DynamicFormsService
  ) {}

  ngOnInit() {
    this.initForm();

    const paymentData: any = this.caseService.paymentData || {};
    this.paymentMethod = paymentData.paymentMethod;
    this.amount = paymentData.amount;
    this.frequency = paymentData.freq;
    this.firstPaymentDate = paymentData.date;
    this.numberOfPayments = Number(paymentData.limit) || 1;
    this.outstanding = paymentData.total;
    this.caseList = paymentData.caseList;

    if(this.planType == 'group_plan') {
      this.caseId = this.caseData?.[0]?.id;
    } else {
      this.caseId = paymentData.caseId;
    }

    let portalSettings = JSON.parse(localStorage.getItem('portal_settings'));
    if (portalSettings?.fieldLabels) {
      this.fieldLabels = portalSettings.fieldLabels;
    }
    if(portalSettings?.gateway_templates?.repay?.ach_recurring_payment) {
      this.paymentGatewayTemplate = portalSettings.gateway_templates.repay.ach_recurring_payment;
    }
    this.achDisclosureEnabled = !!portalSettings?.portal_details?.ach_payment_disclosure;

    this.getDebtorDetails();
    this.getCaseDetails();
    this.commonService.showLoader('Loading checkout form...');
    this.getCheckoutFormId();
  }

  ngOnDestroy() {
    if (this.caseDetailsSubscription) {
      this.caseDetailsSubscription.unsubscribe();
    }
    if (this.debtorDetailsSubscription) {
      this.debtorDetailsSubscription.unsubscribe();
    }
  }

  getDebtorDetails() {
    this.debtorDetailsSubscription = this.authService
      .getdebtorDetailsData()
      .subscribe((res: any) => {
        if (res && res.result) {
          this.debtorDetails = res.data;
          console.log(res.data);
          this.updateFormData(res.data);
          this.billingAddress = res.data.addresses.map((add) => {
            return this.getBillingAddress(add);
          });
          if (this.billingAddress[0]) {
            this.selectedBillingAddress = this.billingAddress[0];
          }
        }
      });
  }

  updateFormData(data) {
    const updatedValues = {
      first_name: data.debtor_1_name  || '',
      last_name: data.debtor_1_surname || '',
      debtor_email: data?.emails?.[0]?.account || data?.debtor_email || '',
      address_ln1: data?.addresses[0]?.address_ln1 || '',
      address_ln2: data?.addresses[0]?.address_ln2 || '',
      address_ln3: data?.addresses[0]?.address_ln3 || '',
      address_town: data?.addresses[0]?.address_town || '',
      address_postcode: data?.addresses[0]?.address_postcode || '',
    };
    Object.keys(updatedValues).forEach((key) => {
      this.addAchForm.controls[key].setValue(updatedValues[key]);
    });
  }

  getCaseDetails() {
    this.caseDetailsSubscription = this.caseService
      .getCaseDetailsData()
      .subscribe((res: any) => {
        if (res && res.result) {
          this.paymentGatewayList = res.data.paymentGatewayList;
        }
      });
  }

  initForm() {
    this.addAchForm = this.formBuilder.group({
      account_number: ['', [Validators.required, Validators.pattern('^[0-9]{4,17}$')]],
      routing_number: ['', [Validators.required, Validators.pattern('^[0-9]{9}$')]],
      account_type: ['Checking', [Validators.required]],
      debtor_email: ['', [Validators.required, Validators.email]],
      first_name: ['', [Validators.required]],
      last_name: ['', [Validators.required]],
      address_ln1: ['', [Validators.required]],
      address_ln2: [''],
      address_ln3: [''],
      address_town: ['', [Validators.required]],
      address_postcode: ['', [Validators.required]],
      description: [''],
    });
  }

  getBillingAddress(add) {
    const address_ln1 = add?.address_ln1 ? add?.address_ln1 + ', ' : '';
    const address_ln2 = add?.address_ln2 ? add?.address_ln2 + ', ' : '';
    const address_ln3 = add?.address_ln3 ? add?.address_ln3 + ', ' : '';
    const address_town = add?.address_town ? add?.address_town + ', ' : '';
    const address_postcode = add?.address_postcode ? add?.address_postcode : '';
    return address_ln1 + address_ln2 + address_ln3 + address_town + address_postcode;
  }

  isAddressMissing() {
    const value = this.addAchForm.value;
    return !value['address_town'] || !value['address_postcode'] || !value['address_ln1'];
  }

  async changeAddress() {
    const values = this.addAchForm.value;
    const address = {
      address_ln1: values.address_ln1,
      address_ln2: values.address_ln2,
      address_ln3: values.address_ln3,
      address_town: values.address_town,
      address_postcode: values.address_postcode,
    };
    const requiredAddress = {
      address_ln1: true,
      address_town: true,
      address_postcode: true,
    };
    const modal = await this.modalController.create({
      component: AddressDetailsPage,
      componentProps: {
        address,
        requiredAddress,
        setAddress: (data) => {
          Object.keys(data).forEach((key) => {
            this.addAchForm.controls[key].setValue(data[key]);
          });
        },
      },
    });
    return await modal.present();
  }

  async getCheckoutFormId() {
    try {
      const response = await this.caseService.getRepayCheckoutForm({
        payment_method: 'ach',
        transaction_type: 'scheduled_sale',
        source: 'Lateral DAPI'
      }).toPromise() as RepayApiResponse;

      if (response?.result) {
        this.checkoutFormId = response.result;
      }
    } catch (error) {
      this.commonService.showToast('Error getting checkout form ID', 'danger');
    } finally {
      this.saveBtnDisabled = false;
      this.commonService.dismissLoader();
    }
  }

  getRepayFrequency(): string {
    const map: { [key: string]: string } = { '3': 'WEEKLY', '4': 'MONTHLY', '6': 'BIWEEKLY' };
    return map[this.frequency] || String(this.frequency || '').toUpperCase();
  }

  // Date of the final installment: start + (count - 1) * frequency period.
  getScheduleEnds(): string {
    const steps = Math.max(Number(this.numberOfPayments) || 1, 1) - 1;
    switch (this.frequency) {
      case '3': return moment(this.firstPaymentDate).add(steps, 'weeks').format('YYYY-MM-DD');      // weekly
      case '6': return moment(this.firstPaymentDate).add(steps * 2, 'weeks').format('YYYY-MM-DD');  // biweekly
      case '4': return moment(this.firstPaymentDate).add(steps, 'months').format('YYYY-MM-DD');     // monthly
      default:  return moment(this.firstPaymentDate).add(steps, 'months').format('YYYY-MM-DD');
    }
  }

  async getPaytoken() {
    if (!this.checkoutFormId) {
      this.commonService.showToast('Checkout form ID not available', 'danger');
      return;
    }

    try {
      const formData = this.addAchForm.value;
      const paytokenRequest = {
        transaction_type: 'scheduled_sale',
        schedule_frequency: this.getRepayFrequency(),
        schedule_interval: 1,
        schedule_starts: moment(this.firstPaymentDate).format('YYYY-MM-DD'),
        amount: this.amount.toString(),
        customer_id: this.debtorDetails.debtor_id,
        name_on_check: `${formData.first_name} ${formData.last_name}`,
        ach_account_number: formData.account_number,
        ach_routing_number: formData.routing_number,
        ach_account_type: formData.account_type,
        sec_code: 'TEL',
        address_street: formData.address_ln1,
        address_city: formData.address_ln3,
        address_state: formData.address_town,
        address_zip: formData.address_postcode,
        channel_user: formData.debtor_email,
        source: 'Lateral DAPI',
        payment_method: 'repay',
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.debtor_email,
        payment_channel: 'web',
        waive_conv_fee: true,
        checkout_form_id: this.checkoutFormId,
        is_group: this.planType == 'group_plan' ? 1 : 0,
        cases: this.caseList,
        case_id: this.caseId,
        freq: this.frequency,
        ref_amount: this.outstanding,
      };

      const response = await this.caseService.getRepayAchPayToken(paytokenRequest).toPromise() as RepayApiResponse;

      if (response?.result) {
        this.paytoken = response.result;
        return true;
      }
      return false;
    } catch (error) {
      this.commonService.showToast('Error getting paytoken', 'danger');
      return false;
    }
  }

  async save() {
    this.addAchForm.markAllAsTouched();

    if (this.addAchForm.valid) {
      if (!moment(this.firstPaymentDate).isValid()) {
        this.commonService.showToast('Please select a valid first payment date.', 'danger');
        return;
      }

      if (this.achDisclosureEnabled) {
        const values = this.addAchForm.getRawValue();
        this.ionNav.push(CustomFormPage, {
          case_id: this.caseId,
          debtor_id: this.debtorDetails.debtor_id,
          form_slug: 'case_form_NACHA_recurring',
          dpa_check: 'dob',
          // Recurring plan: the disclosure reads the first instalment's date.
          variables: buildNachaVariables({
            accountType: values.account_type,
            routingNumber: values.routing_number,
            accountNumber: values.account_number,
            amount: this.amount,
            currencySymbol: this.caseService.getCurrencySymbol(),
            paymentDate: this.firstPaymentDate,
            accountName: `${values.first_name || ''} ${values.last_name || ''}`,
            // The schedule table the debtor just reviewed on this screen.
            scheduleHtml: this.selectedPlanData,
            numberOfPayments: this.numberOfPayments,
            frequency: this.frequency,
          }),
          onFormSubmitted: () => {
            this.proceedWithPayment();
          },
        });
        return;
      }
      await this.proceedWithPayment();
    } else {
      const keys = Object.keys(this.addAchForm.controls);
      for (let key of keys) {
        if (!this.addAchForm.controls[key].valid) {
          if (['address_postcode', 'address_town', 'address_ln1'].includes(key)) {
            this.commonService.showToast(`Address requires ${key} value, please update address!`, 'danger');
            return;
          }
          this.commonService.showToast(`Please check ${key} value!`, 'danger');
          return;
        }
      }
    }
  }

  private async proceedWithPayment() {
      this.commonService.showLoader();

      try {
        const paytokenSuccess = await this.getPaytoken();

        if (!paytokenSuccess) {
          this.commonService.dismissLoader();
          return;
        }

        const formData = this.addAchForm.value;

        const paymentRequest = {
          transaction_type: 'scheduled_sale',
          schedule_frequency: this.getRepayFrequency(),
          schedule_interval: 1,
          schedule_starts: moment(this.firstPaymentDate).format('YYYY-MM-DD'),
          schedule_ends: this.getScheduleEnds(),
          amount: this.amount.toString(),
          customer_id: this.debtorDetails.debtor_id,
          checkout_form_id: this.checkoutFormId,
          paytoken: this.paytoken,
          name_on_check: `${formData.first_name} ${formData.last_name}`,
          ach_account_number: formData.account_number,
          ach_routing_number: formData.routing_number,
          ach_account_type: formData.account_type,
          sec_code: 'TEL',
          address_street: formData.address_ln1,
          address_city: formData.address_ln3,
          address_state: formData.address_town,
          address_zip: formData.address_postcode,
          channel_user: formData.debtor_email,
          payment_channel: 'web',
          source: 'Lateral DAPI',
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.debtor_email,
          is_group: this.planType == 'group_plan' ? 1 : 0,
          cases: this.caseList,
          case_id: this.caseId,
          payment_method: 'repay',
          freq: this.frequency,
          ref_amount: this.outstanding,

        };

        this.caseService.scheduleRepayAchPayment(paymentRequest)
          .subscribe((res: any) => {
            this.saveBtnDisabled = false;
            this.commonService.dismissLoader();

            if (res?.result) {
              this.commonService.showToast(
                res?.message || 'Action Performed Successfully!',
                'success'
              );
              if (this.achDisclosureEnabled) {
                this.dynamicFormsService.sendDisclosureConfirmationEmail('case_form_NACHA_recurring', this.caseId, formData.debtor_email)
                  .subscribe({ error: () => {} });
              }
              this.caseService.saveCaseDetails(localStorage.getItem('debtor_id'));
              this.ionNav.popToRoot();
            } else {
              this.commonService.showToast(
                res?.message || 'Payment failed',
                'danger'
              );
            }
          });
      } catch (error) {
        this.saveBtnDisabled = false;
        this.commonService.dismissLoader();
        this.commonService.showToast('Error processing payment', 'danger');
    }
  }

  async dismiss() {
    this.ionNav.pop();
  }

  toggleOptions(value) {
    this.showOption = (value == false) ? true : false;
  }
}